import fetcher from 'api/fetcher';
import { sleep } from 'utils/clickme';
import { AiImageQueueStatus } from 'utils/constants/banner';

import { fetchRemainingAiImageGenerations } from 'components/common/BannerImg/AiImageGenerationModal/fetchRemainingAiImageGenerations';

import {
    AiImageQueuePosition,
    INITIAL_SELECTORS,
    Selectors,
    setGeneratedImage,
    setGenerationPosition,
    setGenerationQueueId,
    setIsGenerationError,
} from 'store/models/aiImageGeneration';
import { ImageInfoResponse } from 'store/models/createBanner';
import { AppThunkAction } from 'store/utils';

type AiImageGenerationInfoResponse = {
    status: AiImageQueueStatus;
    position: AiImageQueuePosition | null;
    resultImage: ImageInfoResponse | null;
};

const FETCH_INTERVAL = 5000;

const checkGenerationStatus = (aiImageQueueId: number): AppThunkAction<void> => {
    return (dispatch, getState) => {
        const getGenerationStatus = async (): Promise<AiImageGenerationInfoResponse | null> => {
            try {
                const currentAiImageQueueId = getState().aiImageGeneration.aiImageQueueId;

                if (
                    aiImageQueueId === null ||
                    aiImageQueueId !== currentAiImageQueueId ||
                    currentAiImageQueueId === null
                ) {
                    return null;
                }

                const response = await fetcher.get<unknown, AiImageGenerationInfoResponse>(
                    `/api/v1/images/ai/${currentAiImageQueueId}/info/`
                );

                const { status, position } = response;

                dispatch(setGenerationPosition(position));

                if (status === AiImageQueueStatus.New || status === AiImageQueueStatus.InProgress) {
                    await sleep(FETCH_INTERVAL);
                    return await getGenerationStatus();
                }

                return response;
            } catch {
                dispatch([setIsGenerationError(true), setGenerationQueueId(null), setGenerationPosition(null)]);
                return null;
            }
        };

        void getGenerationStatus()
            .then((response) => {
                if (!response) {
                    return;
                }

                const { status, resultImage } = response;

                if (status === AiImageQueueStatus.Success && !!resultImage) {
                    dispatch(setGeneratedImage(resultImage));
                }

                if (status === AiImageQueueStatus.Error) {
                    dispatch(setIsGenerationError(true));
                }
                dispatch(setGenerationPosition(null));
            })
            .catch(() => {
                dispatch([setIsGenerationError(true), setGenerationQueueId(null), setGenerationPosition(null)]);
            })
            .finally(() => {
                void dispatch(fetchRemainingAiImageGenerations());
            });
    };
};

export const generateImage = (profRoleId: string, selectors: Selectors | null): AppThunkAction => {
    return (dispatch) => {
        dispatch(setIsGenerationError(false));

        return fetcher
            .post<unknown, { aiImageQueueId: number }>('/api/v1/images/ai/generate', {
                profRoleId: profRoleId ?? '',
                ...(selectors ?? INITIAL_SELECTORS),
            })
            .then((response) => {
                const { aiImageQueueId } = response;
                dispatch(setGenerationQueueId(aiImageQueueId));
                dispatch(checkGenerationStatus(aiImageQueueId));
            })
            .then(() => {
                void dispatch(fetchRemainingAiImageGenerations());
            })
            .catch(() => {
                dispatch([setGenerationQueueId(null), setIsGenerationError(true)]);
            });
    };
};
